import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export interface Props extends ChakraV3.TabsRootProps {
  routes: string[];
}

export const TabsRouter = (props: Props) => {
  const { routes, children, ...rest } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // The bit after the * bit of the route:
  // participants/123/notes, the splat is notes
  const splat = params['*'] ?? '';

  // Represents the base path of the route:
  // participants/123/notes, the base path is /participants/123
  const basePath = React.useMemo(() => {
    const depth = P.isEmptyString(splat) ? 0 : splat.split('/').length;
    const segments = location.pathname.split('/');

    return segments.slice(0, segments.length - depth).join('/');
  }, [location.pathname, splat]);

  const selectedTab = React.useMemo(() => splat.split('/')[0], [splat]);

  React.useEffect(() => {
    // If the current path is not in the list of routes, redirect to the first route.
    const route = P.first(routes);

    if (!route) {
      return;
    }

    if (P.isNil(selectedTab) || !routes.includes(selectedTab)) {
      void navigate(`${basePath}/${route}`, { replace: true });
    }
  }, [navigate, routes, selectedTab, basePath]);

  const handleTabsChange = React.useCallback(
    (route: string) => {
      if (!route) {
        return;
      }

      void navigate(`${basePath}/${route}`, { replace: true });
    },
    [navigate, basePath],
  );

  return (
    <ChakraV3.Tabs.Root
      value={selectedTab}
      onValueChange={(e) => handleTabsChange(e.value)}
      {...rest}
    >
      {children}
    </ChakraV3.Tabs.Root>
  );
};
